import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  MapPin,
  Calendar,
  Star,
  CheckCircle,
  FileText,
  Clock,
  AlertCircle,
  ChevronRight,
  Shield,
  Activity,
  ChevronDown,
  Award,
  X
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useJobStore } from '@/store/jobStore';
import { backendJobService } from '@/services/backendJobService';
import { quoteService } from '@/services/quoteService';
import { messagingService } from '@/services/messagingService';
import { useGlobalToast } from '@/contexts/ToastContext';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SkipLink } from '@/components/ui/SkipLink';
import { QuickQuoteModal } from '@/components/quote/QuickQuoteModal';
import { QuoteComparison } from '@/components/quote/QuoteComparison';
import { ProviderDetailsModal } from '@/components/job-detail/ProviderDetailsModal';
import { backendProviderService } from '@/services/backendProviderService';
import { FadeIn, ScaleIn } from '@/components/ui/AnimatedComponents';
import { SkeletonCard } from '@/components/ui/SkeletonLoaders';
import type { Job, ServiceProvider } from '@/types';

export function JobDetailPage() {
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { fetchJobs } = useJobStore();
  const { success, error: showError, info } = useGlobalToast();
  const [quotes, setQuotes] = useState<any[]>([]);
  const [confirmedQuote, setConfirmedQuote] = useState<any | null>(null);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [providerQuote, setProviderQuote] = useState<any | null>(null);
  const [isLoadingProviderQuote, setIsLoadingProviderQuote] = useState(false);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState<string | null>(null);
  const [rejectingQuoteId, setRejectingQuoteId] = useState<string | null>(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showDateSelectionModal, setShowDateSelectionModal] = useState(false);
  const [quoteToAccept, setQuoteToAccept] = useState<any | null>(null);
  const [selectedDateSlot, setSelectedDateSlot] = useState<{date: string; time_slot: string} | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null);
  const [isLoadingProviderProfile, setIsLoadingProviderProfile] = useState(false);
  const [isCancellingJob, setIsCancellingJob] = useState(false);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) {
        console.log('❌ No jobId provided');
        setIsLoading(false);
        return;
      }
      
      console.log('🔄 Fetching job details for:', jobId, 'User role:', user?.role);
      setIsLoading(true);
      try {
        let job: Job | null = null;
        
        // Use different endpoint based on user role
        if (user?.role === 'provider') {
          console.log('📞 Calling getProviderJobById...');
          job = await backendJobService.getProviderJobById(jobId);
        } else {
          console.log('📞 Calling getJobById...');
          job = await backendJobService.getJobById(jobId);
        }
        
        console.log('✅ Job fetched:', job);
        setCurrentJob(job);

        // Fetch quotes if customer
        if (user?.role === 'customer' && job) {
          await fetchQuotes(jobId);
        }

        // Check if provider already quoted this job
        if (user?.role === 'provider' && job) {
          await checkProviderQuote(jobId);
        }
      } catch (error) {
        console.error('❌ Failed to fetch job:', error);
        setCurrentJob(null);
      } finally {
        console.log('✅ Setting isLoading to false');
        setIsLoading(false);
      }
    };

    fetchJobDetails();

    // Check if should open quote modal
    if (searchParams.get('action') === 'quote') {
      setShowQuoteModal(true);
    }
  }, [jobId, user?.role, searchParams]);

  const fetchQuotes = async (jobId: string) => {
    try {
      setIsLoadingQuotes(true);
      console.log('🔄 Fetching quotes for job:', jobId);
      const quotesData = await quoteService.getQuotesForJob(jobId);
      console.log('✅ Quotes fetched:', quotesData);
      // Log each quote's available_date_slots for debugging
      quotesData.forEach((q: any, index: number) => {
        console.log(`Quote ${index + 1} (${q.id}):`, {
          hasAvailableDateSlots: !!q.available_date_slots,
          availableDateSlots: q.available_date_slots,
          availableDateSlotsLength: q.available_date_slots?.length
        });
      });
      setQuotes(quotesData);
      
      // Check if any quote is accepted - this will hide the "Action required" panel
      const accepted = quotesData.find((q: any) => q.status === 'accepted');
      if (accepted) {
        console.log('✅ Found accepted quote, setting confirmedQuote:', accepted.id);
        setConfirmedQuote(accepted);
      } else {
        // Clear confirmedQuote if no accepted quote found (shouldn't happen after acceptance, but for safety)
        console.log('ℹ️ No accepted quote found, clearing confirmedQuote');
        setConfirmedQuote(null);
      }
    } catch (error) {
      console.error('❌ Failed to fetch quotes:', error);
      setQuotes([]);
      setConfirmedQuote(null);
    } finally {
      setIsLoadingQuotes(false);
    }
  };

  // Determine if we should show comparison view (2+ quotes)
  const shouldShowComparison = quotes.length > 1;

  const handleMessageProvider = async (providerId: string, providerName: string) => {
    if (!jobId || !user || !currentJob) return;
    
    try {
      // Determine customer and provider IDs
      const customerId = user.id;
      const customerName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Customer';
      
      // Create or get chat conversation
      const chat = await messagingService.createOrGetJobChat(
        jobId,
        currentJob.title,
        customerId,
        customerName,
        providerId,
        providerName,
        providerName
      );
      
      // Navigate to chat page
      navigate(`/chat/${chat.id}`);
    } catch (err: any) {
      console.error('Failed to create chat:', err);
      showError('Failed to Start Conversation', 'Please try again.');
    }
  };

  const checkProviderQuote = async (jobId: string) => {
    try {
      setIsLoadingProviderQuote(true);
      console.log('🔄 Checking if provider already quoted job:', jobId);
      
      // Get all provider's quotes and find one for this job
      const allQuotes = await quoteService.getMyQuotes();
      const existingQuote = allQuotes.find((q: any) => q.job_id === jobId);
      
      if (existingQuote) {
        console.log('✅ Provider already quoted this job:', existingQuote);
        setProviderQuote(existingQuote);
      }
    } catch (error) {
      console.error('❌ Failed to check provider quote:', error);
    } finally {
      setIsLoadingProviderQuote(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <FadeIn duration={200}>
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading job details...</p>
          </div>
        </FadeIn>
      </div>
    );
  }

  if (!currentJob) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Job Not Found</h2>
          <p className="text-gray-600 mb-4">The job you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const isCustomer = user?.role === 'customer';
  const isProvider = user?.role === 'provider';
  const isJobOwner = isCustomer && currentJob.customerId === user?.id;
  const isIndividualProvider = isProvider && user?.providerType === 'individual';

  const handleAcceptQuote = async (quoteId: string, dateSlot?: {date: string; time_slot: string}) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote) {
      showError('Error', 'Quote not found');
      return;
    }

    const providerName = quote.provider?.business_name || 'this provider';
    const amount = quote.amount;

    // Debug: Log quote data to check available_date_slots
    console.log('🔍 Checking quote for date slots:', {
      quoteId: quote.id,
      hasAvailableDateSlots: !!quote.available_date_slots,
      availableDateSlots: quote.available_date_slots,
      availableDateSlotsLength: quote.available_date_slots?.length,
      dateSlot: dateSlot,
      quoteObject: quote
    });

    // If quote has available_date_slots but no dateSlot selected, show modal
    // Check for both array with length > 0 AND that it's not an empty array
    if (quote.available_date_slots && Array.isArray(quote.available_date_slots) && quote.available_date_slots.length > 0 && !dateSlot) {
      console.log('📅 Showing date selection modal with slots:', quote.available_date_slots);
      setQuoteToAccept(quote);
      setShowDateSelectionModal(true);
      return;
    }

    // If we reach here without a dateSlot but backend requires one, show error
    if (!dateSlot && quote.available_date_slots && Array.isArray(quote.available_date_slots) && quote.available_date_slots.length > 0) {
      console.error('❌ Date slot required but not provided');
      showError('Date Selection Required', 'This provider has offered multiple time slots. Please select one to continue.');
      return;
    }

    setAcceptingQuoteId(quoteId);
    try {
      console.log('🔄 Accepting quote:', quoteId, 'with date slot:', dateSlot);
      
      // Call backend to accept quote with selected date slot
      await quoteService.acceptQuote(quoteId, dateSlot);
      
      console.log('✅ Quote accepted successfully');
      
      // Refetch job and quotes - fetchQuotes will automatically set confirmedQuote
      if (jobId) {
        // Fetch job first to get updated status (should be 'in-progress' now)
        const job = await backendJobService.getJobById(jobId);
        setCurrentJob(job);
        
        // Fetch quotes - this will automatically find and set confirmedQuote from server
        // This will hide the "Action required" panel (quotes section) because confirmedQuote will be set
        await fetchQuotes(jobId);
        
        // Ensure confirmedQuote is set from the updated quotes to hide the quotes section
        // fetchQuotes should have already set it, but double-check for safety
        const updatedQuotes = await quoteService.getQuotesForJob(jobId);
        const acceptedQuote = updatedQuotes.find((q: any) => q.status === 'accepted');
        if (acceptedQuote) {
          console.log('✅ Setting confirmedQuote from updated quotes:', acceptedQuote.id);
          setConfirmedQuote(acceptedQuote);
        } else {
          console.warn('⚠️ No accepted quote found in updated quotes - this should not happen');
        }
        
        // Refresh job store so CustomerDashboardPage shows updated data
        // This will update the "Action Required" panel on the dashboard
        try {
          await fetchJobs();
          console.log('✅ Job store refreshed - dashboard will show updated job status');
        } catch (err) {
          console.warn('⚠️ Failed to refresh job store (non-critical):', err);
        }
      }
      
      // Show success toast (this replaces the window.confirm dialog)
      success(
        'Quote Accepted', 
        `${providerName} has been notified and will contact you soon.`
      );
    } catch (err: any) {
      console.error('❌ Failed to accept quote:', err);
      
      // Check if error is about missing date slot
      const errorMsg = err?.message || err?.error || 'Failed to accept quote';
      if (errorMsg.includes('selected_date_slot is required') || errorMsg.includes('available_date_slots')) {
        showError(
          'Date Selection Required', 
          'This provider has offered multiple time slots. Please select a preferred date and time to continue.'
        );
        // Try to show the modal if we have the quote
        const quote = quotes.find(q => q.id === quoteId);
        if (quote && quote.available_date_slots && quote.available_date_slots.length > 0) {
          setQuoteToAccept(quote);
          setShowDateSelectionModal(true);
        }
      } else {
        const errorMessage = typeof errorMsg === 'string' ? errorMsg : 'Failed to accept quote. Please try again.';
        showError('Quote Acceptance Failed', errorMessage);
      }
    } finally {
      setAcceptingQuoteId(null);
    }
  };

  const handleConfirmDateSelection = async () => {
    console.log('🔵 handleConfirmDateSelection called');
    console.log('🔵 quoteToAccept:', quoteToAccept?.id);
    console.log('🔵 selectedDateSlot:', selectedDateSlot);
    
    if (!quoteToAccept || !selectedDateSlot) {
      console.error('❌ Missing quoteToAccept or selectedDateSlot');
      showError('Error', 'Please select a date and time slot');
      return;
    }

    console.log('✅ Proceeding with quote acceptance');
    setShowDateSelectionModal(false);
    await handleAcceptQuote(quoteToAccept.id, selectedDateSlot);
    setQuoteToAccept(null);
    setSelectedDateSlot(null);
  };

  const handleRejectQuote = async (quoteId: string) => {
    if (!confirm('Are you sure you want to reject this quote?')) {
      return;
    }

    setRejectingQuoteId(quoteId);
    try {
      await quoteService.rejectQuote(quoteId);
      if (jobId) {
        await fetchQuotes(jobId);
      }
      success('Quote Rejected', 'The quote has been rejected successfully.');
    } catch (err: any) {
      console.error('❌ Failed to reject quote:', err);
      const errorMessage = err?.message || 'Failed to reject quote. Please try again.';
      showError('Quote Rejection Failed', errorMessage);
    } finally {
      setRejectingQuoteId(null);
    }
  };

  const handleViewProviderProfile = async (providerId?: string) => {
    // Use provider ID from quote if available, otherwise from parameter
    const id = providerId || confirmedQuote?.provider?.id || confirmedQuote?.provider_id;
    
    if (!id) {
      showError('Error', 'Provider ID is missing');
      return;
    }

    setIsLoadingProviderProfile(true);
    try {
      console.log('🔄 Fetching provider profile for ID:', id);
      
      // Try to fetch full provider profile from backend
      let provider = await backendProviderService.getProviderById(id);
      
      // If full profile not found, create a basic provider object from quote data
      if (!provider && confirmedQuote?.provider) {
        console.log('⚠️ Full provider profile not found, using quote provider data');
        const quoteProvider = confirmedQuote.provider;
        
        // Create a basic ServiceProvider object from quote provider data
        provider = {
          id: id,
          userId: id,
          businessName: quoteProvider.business_name || 'Provider',
          bio: quoteProvider.bio || '',
          verified: quoteProvider.verified || false,
          availableNow: false,
          location: currentJob?.location || '',
          rating: quoteProvider.rating || 0,
          reviewCount: quoteProvider.review_count || 0,
          pricing: {
            hourlyRate: 0,
            calloutFee: 0
          },
          serviceCategories: quoteProvider.specializations || [],
          profileImage: undefined,
          providerType: quoteProvider.provider_type || 'INDIVIDUAL',
          isPremium: false,
          subscriptionTier: 'BASIC',
          insurance: {
            hasInsurance: quoteProvider.has_insurance || false,
            provider: quoteProvider.insurance_provider,
            coverage: quoteProvider.insurance_coverage,
            expiryDate: undefined
          },
          certifications: quoteProvider.certifications || [],
          portfolio: [],
          yearsOfExperience: quoteProvider.years_of_experience || 0,
          availability: {
            nextAvailable: new Date(),
            workingHours: {},
            emergencyService: false,
            weekendService: false
          },
          completedJobs: 0,
          responseTime: 'Within 24h',
          languages: ['English'],
          backgroundCheck: false,
          createdAt: new Date(),
          updatedAt: new Date()
        };
      }
      
      if (!provider) {
        showError('Error', 'Provider profile not found');
        return;
      }

      console.log('✅ Provider profile ready:', provider);
      setSelectedProvider(provider);
      setShowProviderModal(true);
    } catch (err: any) {
      console.error('❌ Failed to fetch provider profile:', err);
      
      // Fallback: Use quote provider data if fetch fails
      if (confirmedQuote?.provider) {
        console.log('⚠️ Using quote provider data as fallback');
        const quoteProvider = confirmedQuote.provider;
        const fallbackProvider: ServiceProvider = {
          id: id,
          userId: id,
          businessName: quoteProvider.business_name || 'Provider',
          bio: quoteProvider.bio || '',
          verified: quoteProvider.verified || false,
          availableNow: false,
          location: currentJob?.location || '',
          rating: quoteProvider.rating || 0,
          reviewCount: quoteProvider.review_count || 0,
          pricing: { hourlyRate: 0, calloutFee: 0 },
          serviceCategories: quoteProvider.specializations || [],
          providerType: quoteProvider.provider_type || 'INDIVIDUAL',
          insurance: {
            hasInsurance: quoteProvider.has_insurance || false,
            provider: quoteProvider.insurance_provider,
            coverage: quoteProvider.insurance_coverage
          },
          certifications: quoteProvider.certifications || [],
          yearsOfExperience: quoteProvider.years_of_experience || 0,
          availability: { nextAvailable: new Date(), workingHours: {}, emergencyService: false, weekendService: false },
          completedJobs: 0,
          responseTime: 'Within 24h',
          languages: ['English'],
          backgroundCheck: false,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        setSelectedProvider(fallbackProvider);
        setShowProviderModal(true);
      } else {
        const errorMessage = err?.message || 'Failed to load provider profile. Please try again.';
        showError('Error', errorMessage);
      }
    } finally {
      setIsLoadingProviderProfile(false);
    }
  };

  const handleQuoteSent = async (quote: any) => {
    console.log('✅ Quote sent successfully:', quote);
    setShowQuoteModal(false);
    
    // Update provider quote state
    setProviderQuote({
      ...quote,
      status: 'pending',
      created_at: new Date().toISOString()
    });
    
    // Note: Success toast is already shown by QuickQuoteModal
    // No need to show alert popup here
  };

  const handleCancelJob = async () => {
    if (!jobId) return;
    if (!confirm('Are you sure you want to cancel this job? This will permanently delete it.')) return;
    setIsCancellingJob(true);
    try {
      await backendJobService.deleteJob(jobId);
      success('Job deleted successfully');
      navigate('/jobs');
    } catch (err: any) {
      showError(err.message || 'Failed to cancel job');
    } finally {
      setIsCancellingJob(false);
    }
  };

  const statusBadgeVariant = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'OPEN': return 'available';
      case 'IN_PROGRESS': return 'warning';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'neutral';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[
          { 
            label: isIndividualProvider ? 'Dashboard' : (isProvider ? 'Job Board' : 'My Jobs'), 
            href: isIndividualProvider ? '/dashboard' : (isProvider ? '/provider/jobs' : '/dashboard')
          },
          { label: currentJob.title }
        ]} />

        {/* Job Header */}
        <div className="mb-8 mt-4">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Badge variant={statusBadgeVariant(currentJob.status)}>
                  {currentJob.status.replace('_', ' ')}
                </Badge>
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Posted {new Date(currentJob.createdAt).toLocaleDateString()}
                </span>
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {currentJob.location}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{currentJob.title}</h1>
            </div>

            {isJobOwner && currentJob.status.toUpperCase() === 'OPEN' && (
              <Button
                variant="outline"
                icon={<AlertCircle className="w-4 h-4" />}
                onClick={handleCancelJob}
                disabled={isCancellingJob}
              >
                {isCancellingJob ? 'Cancelling...' : 'Cancel Job'}
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">

            {/* Job Details Card */}
            <Card>
              <CardHeader title="Job Details" icon={<FileText className="w-5 h-5 text-gray-500" />} />
              <div className="p-6 pt-0 space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                    {currentJob.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Job Specifics</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Category:</span>
                        <span className="font-medium text-gray-900">{currentJob.category}</span>
                      </li>
                      <li className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Urgency:</span>
                        <span className="font-medium text-gray-900 capitalize">{currentJob.urgency.toLowerCase()}</span>
                      </li>
                      <li className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Budget:</span>
                        <span className="font-medium text-gray-900">
                          {currentJob.budgetRange
                            ? `€${currentJob.budgetRange.min} - €${currentJob.budgetRange.max}`
                            : 'Not specified'}
                        </span>
                      </li>
                    </ul>
                  </div>

                  {currentJob.images && currentJob.images.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Photos</h3>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {currentJob.images.map((img: string, idx: number) => (
                          <div key={idx} className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                            <img src={img} alt={`Job photo ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Confirmed Provider Section (if any) */}
            {confirmedQuote && (
              <FadeIn delay={100} duration={500}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Assigned Professional</h2>
                  <Badge variant="success" icon={<CheckCircle className="w-3 h-3" />}>Job In Progress</Badge>
                </div>
                <Card className="border-success-200 bg-success-50 hover:shadow-md transition-shadow duration-200">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {confirmedQuote.provider?.business_name || 'Service Provider'}
                        </h3>
                        <div className="flex items-center gap-2 mt-2">
                          {confirmedQuote.provider?.verified && (
                            <Badge variant="success" size="sm">Verified</Badge>
                          )}
                          {confirmedQuote.provider?.rating && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              <span>{confirmedQuote.provider.rating.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-success-600">€{confirmedQuote.amount}</div>
                        {confirmedQuote.estimated_duration && (
                          <div className="text-sm text-gray-500">{confirmedQuote.estimated_duration}</div>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{confirmedQuote.description}</p>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={() => {
                          if (confirmedQuote?.provider?.id) {
                            handleMessageProvider(
                              confirmedQuote.provider.id,
                              confirmedQuote.provider.business_name || 'Provider'
                            );
                          }
                        }}
                      >
                        Message Provider
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewProviderProfile()}
                        disabled={isLoadingProviderProfile || (!confirmedQuote?.provider?.id && !confirmedQuote?.provider_id)}
                      >
                        {isLoadingProviderProfile ? 'Loading...' : 'View Profile'}
                      </Button>
                    </div>
                  </div>
                </Card>
              </FadeIn>
            )}

            {/* Quotes Section (for Customer) */}
            {isJobOwner && !confirmedQuote && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    {shouldShowComparison ? 'Compare Quotes' : 'Quotes Received'} ({quotes.length})
                  </h2>
                </div>

                {isLoadingQuotes ? (
                  <Card padding="lg" className="text-center">
                    <div className="w-16 h-16 border-4 border-gray-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading quotes...</p>
                  </Card>
                ) : quotes.length > 0 ? (
                  shouldShowComparison ? (
                    <QuoteComparison
                      quotes={quotes}
                      onAcceptQuote={handleAcceptQuote}
                      onRejectQuote={handleRejectQuote}
                      onMessageProvider={handleMessageProvider}
                      acceptingQuoteId={acceptingQuoteId}
                      rejectingQuoteId={rejectingQuoteId}
                    />
                  ) : (
                    <div className="space-y-4">
                      {quotes.map((quote) => (
                      <div key={quote.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {quote.provider?.business_name || 'Service Provider'}
                              </h3>
                              {quote.provider?.provider_type === 'COMPANY' && (
                                <Badge variant="neutral" size="sm">Company</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {quote.provider?.verified && (
                                <Badge variant="success" size="sm">Verified</Badge>
                              )}
                              {quote.provider?.has_insurance && (
                                <Badge variant="verified" size="sm">
                                  <Shield className="w-3 h-3 mr-1" />
                                  Insured
                                </Badge>
                              )}
                              {quote.provider?.rating && (
                                <div className="flex items-center gap-1 text-sm text-gray-600">
                                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                  <span>{quote.provider.rating.toFixed(1)}</span>
                                  {quote.provider?.review_count && (
                                    <span className="text-gray-400">({quote.provider.review_count})</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-primary-600">€{quote.amount}</div>
                            {quote.estimated_duration && (
                              <div className="text-sm text-gray-500">{quote.estimated_duration}</div>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-gray-600 mb-4">{quote.description}</p>
                        
                        {/* Provider Profile Details - Expandable Section */}
                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <details className="group">
                            <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center justify-between list-none">
                              <span>View Provider Details</span>
                              <ChevronDown className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
                            </summary>
                            
                            <div className="mt-4 space-y-3">
                              {/* Business Info */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {quote.provider?.years_of_experience > 0 && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 block mb-1">Experience</span>
                                    <p className="text-sm text-gray-900">{quote.provider.years_of_experience} years</p>
                                  </div>
                                )}
                                {quote.provider?.provider_type && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 block mb-1">Type</span>
                                    <p className="text-sm text-gray-900">
                                      {quote.provider.provider_type === 'COMPANY' ? 'Company' : 'Individual'}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {/* Insurance Information */}
                              {quote.provider?.has_insurance && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Shield className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-900">Insurance Coverage</span>
                                  </div>
                                  {quote.provider?.insurance_provider && (
                                    <p className="text-sm text-blue-800 mb-1">
                                      <strong>Provider:</strong> {quote.provider.insurance_provider}
                                    </p>
                                  )}
                                  {quote.provider?.insurance_coverage && (
                                    <p className="text-sm text-blue-800">
                                      <strong>Coverage:</strong> {quote.provider.insurance_coverage}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Specializations/Services */}
                              {quote.provider?.specializations && Array.isArray(quote.provider.specializations) && quote.provider.specializations.length > 0 && (
                                <div>
                                  <span className="text-xs font-medium text-gray-500 mb-2 block">Specializations & Services</span>
                                  <div className="flex flex-wrap gap-2">
                                    {quote.provider.specializations.map((spec: string, idx: number) => (
                                      <Badge key={idx} variant="neutral" size="sm">
                                        {spec}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Certifications */}
                              {quote.provider?.certifications && Array.isArray(quote.provider.certifications) && quote.provider.certifications.length > 0 && (
                                <div>
                                  <span className="text-xs font-medium text-gray-500 mb-2 block">Certifications & Qualifications</span>
                                  <div className="flex flex-wrap gap-2">
                                    {quote.provider.certifications.map((cert: string, idx: number) => (
                                      <Badge key={idx} variant="neutral" size="sm">
                                        <Award className="w-3 h-3 mr-1" />
                                        {cert}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Bio */}
                              {quote.provider?.bio && (
                                <div>
                                  <span className="text-xs font-medium text-gray-500 mb-1 block">About</span>
                                  <p className="text-sm text-gray-700">{quote.provider.bio}</p>
                                </div>
                              )}
                            </div>
                          </details>
                        </div>
                        
                        {quote.status === 'pending' && (
                          <div className="flex gap-2 mt-4">
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleAcceptQuote(quote.id)}
                              disabled={acceptingQuoteId === quote.id || rejectingQuoteId !== null}
                            >
                              {acceptingQuoteId === quote.id ? 'Accepting...' : 'Accept Quote'}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleRejectQuote(quote.id)}
                              disabled={rejectingQuoteId === quote.id || acceptingQuoteId !== null}
                            >
                              {rejectingQuoteId === quote.id ? 'Rejecting...' : 'Reject'}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                if (quote.provider?.id) {
                                  handleMessageProvider(
                                    quote.provider.id,
                                    quote.provider.business_name || 'Provider'
                                  );
                                }
                              }}
                            >
                              Message
                            </Button>
                          </div>
                        )}
                        
                        {quote.status !== 'pending' && (
                          <div className="mt-4">
                            <Badge 
                              variant={
                                quote.status === 'accepted' ? 'success' :
                                quote.status === 'rejected' ? 'error' :
                                'neutral'
                              }
                            >
                              {quote.status.toUpperCase()}
                            </Badge>
                          </div>
                        )}
                      </div>
                    ))}
                    </div>
                  )
                ) : (
                  <Card padding="lg" className="text-center bg-gray-50 border-dashed">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-200">
                      <Clock className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Waiting for quotes</h3>
                    <p className="text-gray-500 max-w-sm mx-auto">
                      We've notified professionals in your area. Quotes usually arrive within 2-4 hours.
                    </p>
                  </Card>
                )}
              </div>
            )}

            {/* Quote Action (for Provider) */}
            {isProvider && !confirmedQuote && currentJob && (
              <>
                {providerQuote ? (
                  <Card className="border-primary-200 bg-primary-50">
                    <CardHeader 
                      title="Your Quote" 
                      subtitle="You've already submitted a quote for this job"
                    />
                    <div className="p-6 pt-0">
                      <div className="bg-white rounded-lg p-4 mb-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="text-2xl font-bold text-primary-600">€{providerQuote.amount}</div>
                            {providerQuote.estimated_duration && (
                              <div className="text-sm text-gray-500">{providerQuote.estimated_duration}</div>
                            )}
                          </div>
                          <Badge 
                            variant={
                              providerQuote.status === 'accepted' ? 'success' :
                              providerQuote.status === 'rejected' ? 'error' :
                              'warning'
                            }
                          >
                            {providerQuote.status.toUpperCase()}
                          </Badge>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{providerQuote.description}</p>
                        <div className="text-xs text-gray-500">
                          Submitted {new Date(providerQuote.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      
                      {providerQuote.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setShowQuoteModal(true);
                            }}
                          >
                            Edit Quote
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              if (confirm('Are you sure you want to withdraw this quote?')) {
                                try {
                                  await quoteService.withdrawQuote(providerQuote.id);
                                  setProviderQuote(null);
                                  success('Quote Withdrawn', 'Your quote has been withdrawn successfully.');
                                } catch (err: any) {
                                  const errorMessage = err?.message || 'Failed to withdraw quote. Please try again.';
                                  showError('Withdrawal Failed', errorMessage);
                                }
                              }
                            }}
                          >
                            Withdraw Quote
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader title="Submit a Quote" subtitle="Send your best price to win this job" />
                    <div className="p-6 pt-0">
                      <p className="text-gray-600 mb-6">
                        Review the job details above carefully. Provide a competitive price and estimated duration.
                      </p>
                      <Button 
                        fullWidth 
                        size="lg" 
                        variant="primary"
                        onClick={() => setShowQuoteModal(true)}
                        disabled={isLoadingProviderQuote}
                      >
                        {isLoadingProviderQuote ? 'Checking...' : 'Send Quote'}
                      </Button>
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>

          <div className="lg:col-span-1 space-y-6">
            {/* Timeline */}
            <Card>
              <CardHeader title="Status Timeline" icon={<Activity className="w-5 h-5 text-primary-600" />} />
              <div className="p-6 pt-0">
                <div className="relative border-l-2 border-gray-100 ml-3 space-y-8 pb-2">
                  <div className="relative pl-6">
                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-primary-600 border-2 border-white shadow-sm" />
                    <p className="text-sm font-semibold text-gray-900">Job Posted</p>
                    <p className="text-xs text-gray-500 mt-1">{new Date(currentJob.createdAt).toLocaleDateString()}</p>
                  </div>

                  <div className={`relative pl-6 ${confirmedQuote || currentJob.status.toLowerCase() === 'in_progress' ? '' : 'opacity-50'}`}>
                    <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 border-white ${
                      confirmedQuote || currentJob.status.toLowerCase() === 'in_progress' 
                        ? 'bg-primary-600 shadow-sm' 
                        : 'bg-gray-200'
                    }`} />
                    <p className={`text-sm font-semibold ${
                      confirmedQuote || currentJob.status.toLowerCase() === 'in_progress' 
                        ? 'text-gray-900' 
                        : 'text-gray-500'
                    }`}>
                      Provider Assigned
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {confirmedQuote 
                        ? confirmedQuote.provider?.business_name || 'Provider assigned'
                        : 'Pending selection'}
                    </p>
                  </div>

                  <div className={`relative pl-6 ${currentJob.status.toLowerCase() === 'completed' ? '' : 'opacity-50'}`}>
                    <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 border-white ${
                      currentJob.status.toLowerCase() === 'completed' 
                        ? 'bg-success-600 shadow-sm' 
                        : 'bg-gray-200'
                    }`} />
                    <p className={`text-sm font-semibold ${
                      currentJob.status.toLowerCase() === 'completed' 
                        ? 'text-gray-900' 
                        : 'text-gray-500'
                    }`}>
                      Job Completed
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

      {/* Quote Modal */}
      {currentJob && (
        <QuickQuoteModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          job={currentJob}
          onQuoteSent={handleQuoteSent}
          existingQuote={providerQuote}
        />
      )}

      {/* Provider Profile Modal */}
      {showProviderModal && selectedProvider && (
        <ProviderDetailsModal
          provider={selectedProvider}
          onClose={() => {
            setShowProviderModal(false);
            setSelectedProvider(null);
          }}
          onMessage={(providerId, providerName, providerBusinessName) => {
            handleMessageProvider(providerId, providerName);
            setShowProviderModal(false);
            setSelectedProvider(null);
          }}
        />
      )}

      {/* Date Selection Modal for Quote Acceptance */}
      {showDateSelectionModal && quoteToAccept && quoteToAccept.available_date_slots && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Select Date & Time</h2>
                <p className="text-sm text-gray-600 mt-1">Choose one of the available time slots</p>
              </div>
              <button
                onClick={() => {
                  setShowDateSelectionModal(false);
                  setQuoteToAccept(null);
                  setSelectedDateSlot(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <div className="space-y-3">
                {quoteToAccept.available_date_slots.map((slot: any, index: number) => {
                  // Normalize the slot data - handle multiple formats
                  const dateSlot = {
                    date: slot.date || slot.Date,
                    time_slot: slot.time_slot || slot.TimeSlot || slot.time || ''
                  };
                  
                  console.log(`📅 Slot ${index}:`, dateSlot);
                  
                  const isSelected = selectedDateSlot?.date === dateSlot.date && 
                                     selectedDateSlot?.time_slot === dateSlot.time_slot;
                  const slotDate = new Date(dateSlot.date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isPast = slotDate < today;

                  // Display label for time slot
                  let displayLabel = dateSlot.time_slot;
                  
                  // If it looks like a time range (HH:MM-HH:MM), format nicely
                  if (dateSlot.time_slot && /^\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}$/.test(dateSlot.time_slot)) {
                    const parts = dateSlot.time_slot.split('-');
                    displayLabel = `${parts[0].trim()} - ${parts[1].trim()}`;
                  }
                  // Otherwise capitalize first letter of labels
                  else if (dateSlot.time_slot) {
                    displayLabel = dateSlot.time_slot.charAt(0).toUpperCase() + dateSlot.time_slot.slice(1);
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => {
                        if (!isPast && dateSlot.date && dateSlot.time_slot) {
                          console.log('🎯 Selected date slot:', dateSlot);
                          setSelectedDateSlot(dateSlot);
                        }
                      }}
                      disabled={isPast}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-primary-600 bg-primary-50' 
                          : isPast
                          ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="w-4 h-4 text-gray-600" />
                            <span className="font-semibold text-gray-900">
                              {slotDate.toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </span>
                            {isPast && (
                              <span className="text-xs text-red-600 font-medium">(Past)</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{displayLabel}</span>
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-6 h-6 text-primary-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {!selectedDateSlot && (
                <p className="text-sm text-gray-500 mt-4 text-center">
                  Please select a date and time slot above
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowDateSelectionModal(false);
                  setQuoteToAccept(null);
                  setSelectedDateSlot(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDateSelection}
                disabled={!selectedDateSlot}
                className={`px-6 py-2 rounded-lg font-medium min-h-touch ${
                  selectedDateSlot
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                aria-label="Accept quote with selected date"
              >
                Accept Quote
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
    </div>
  );
}
